import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoginFormData, LoginSchema } from "../../types/forms/auth/loginFormData.form";
import { useTranslation } from "react-i18next";
import { getSignInErrorCode, SignInErrorCode, useLogin } from "@/api/auth.api";
import { Button } from "@/components/ui/button";
import { useAuthRedirect } from "@/hooks/useAuthRedirect";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";

const LoginPage: React.FC = () => {
  const { mutateAsync: login, isPending } = useLogin();
  useAuthRedirect();

  const { t } = useTranslation("boClients");
  const [loginError, setLoginError] = useState<SignInErrorCode | "UNKNOWN" | undefined>();

  const { control, handleSubmit } = useForm<LoginFormData>({
    resolver: zodResolver(LoginSchema),
    mode: "onSubmit",
    defaultValues: { email: "", password: "" },
  });

  const submitForm: SubmitHandler<LoginFormData> = async (data) => {
    setLoginError(undefined);

    try {
      await login(data);
    } catch (error) {
      setLoginError(getSignInErrorCode(error) ?? "UNKNOWN");
    }
  };

  const renderLoginError = () => {
    switch (loginError) {
      case undefined:
        return null;
      case "INVALID_CREDENTIALS":
        return t("authApi.invalidCredentials");
      case "COMPANY_INACTIVE":
        return t("authApi.accountBlocked");
      case "COMPANY_UNPAID_INVOICE":
        return (
          <>
            <span className="block font-semibold">{t("authApi.unpaidBlockedTitle")}</span>
            <span className="block">{t("authApi.unpaidBlockedDescription")}</span>
            <span className="block">{t("authApi.unpaidBlockedHelp")}</span>
          </>
        );
      default:
        return t("authApi.loginError");
    }
  };

  return (
    <div className="bg-brand600 flex min-h-screen w-screen items-center justify-center">
      <div className="mx-4 flex max-w-[600px] flex-col items-center gap-4 rounded-2xl bg-white px-6 py-6 md:px-16 md:py-12 xl:min-h-[500px] xl:min-w-[600px]">
        <img src="/images/pelican-logo.png" className="h-24 w-24 sm:h-32 sm:w-32" />
        <div className="flex w-full flex-col items-center gap-3">
          <h1 className="w-full text-center text-lg font-medium text-black sm:text-xl lg:text-start">
            Connectez-vous à votre compte
          </h1>
          <p className="w-full text-center text-sm text-gray-600 lg:text-start">
            Entrez vos informations pour accéder à votre espace.
          </p>
        </div>

        <form className="flex w-full flex-col gap-4" onSubmit={handleSubmit(submitForm)}>
          <ControlledUnifiedInput
            label="Email"
            control={control}
            name="email"
            autoComplete="username"
          />

          <ControlledUnifiedInput
            label="Mot de passe"
            control={control}
            name="password"
            inputType="password"
            autoComplete="current-password"
          />
          <Link to="/forgot-password">Mot de passe oublié ?</Link>

          {loginError ? (
            <p className="text-center text-xs text-red-500">{renderLoginError()}</p>
          ) : null}

          <div className="flex w-full flex-col gap-2">
            <Button disabled={isPending}>{isPending ? <StyledSpinner /> : "Se connecter"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
